import type { BenchLevel, HardnessDomainCollection, HardnessDomainFeature } from '~api/services';
import { getHardnessColour } from '~config';
import type { CoordinateTransformer, Point } from '~types';
import { polygonCenterToSVG } from '~utils';
import { Label, Polygon, useSvgContext } from '../components';

type HardnessZonesLayerProps = {
  /** Hardness domain GeoJSON feature collection */
  domains: HardnessDomainCollection;
  /** which horizontal plane within the bench to render */
  benchLevel: BenchLevel;
};

type HardnessZoneProps = {
  /** Hardness domain GeoJSON feature */
  feature: HardnessDomainFeature;
  /** Coordinate transformer */
  transformer: CoordinateTransformer;
};

/**
 * Renders a single hardness zone polygon with its label.
 * The polygon is styled based on its hardness level, and a label is placed at the center of the zone.
 *
 * @example
 * ```tsx
 * <HardnessZone feature={zoneFeature} transformer={transformer} />
 * ```
 */
const HardnessZone = ({ feature, transformer }: HardnessZoneProps) => {
  const { hardness } = feature.properties;
  if (!hardness) return null;

  const colour = getHardnessColour(hardness);
  const coordinates = feature.geometry.coordinates[0];
  if (!coordinates?.length) return null;

  const coords: Point[] = coordinates.map((pos) => [pos[0], pos[1]] as Point);
  const center = polygonCenterToSVG(coords, transformer);
  if (!center) return null;

  return (
    <g className={`hardness-zone hardness-zone--${(hardness ?? 'unknown').toLowerCase()}`}>
      <Polygon
        geometry={feature.geometry}
        title={`${colour.label} zone`}
        fill={colour.fill}
        fillOpacity={colour.opacity}
        stroke={colour.stroke}
        strokeWidth={1}
        strokeOpacity={0.8}
      />
      <Label x={center.x} y={center.y} text={colour.label} fontSize={4} fontWeight={600} fill={colour.stroke} />
    </g>
  );
};

/**
 * Renders hardness zones as colored polygons.
 * Each zone is colored based on its hardness level (soft, medium, hard, extra hard).
 *
 * @example
 * ```tsx
 * <HardnessZonesLayer domains={hardnessDomainData.domain} />
 * ```
 */
const HardnessZonesLayer = ({ domains, benchLevel }: HardnessZonesLayerProps) => {
  const { transformer } = useSvgContext();

  if (!transformer || !domains?.features?.length) return null;

  const features = domains.features.filter((d) => d.properties.bench_level === benchLevel);

  return (
    <g className="hardness-zones">
      {features.map((feature, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: Static list that does not change order.
        <HardnessZone key={`hardness-zone-${benchLevel}-${index}`} feature={feature} transformer={transformer} />
      ))}
    </g>
  );
};
HardnessZonesLayer.displayName = 'HardnessZonesLayer';

export { HardnessZonesLayer, type HardnessZonesLayerProps };
